import {
  Books,
  ChartLineUp,
  Compass,
  GearSix,
  MagnifyingGlass,
  Rss,
  SignIn,
  SunHorizon,
  UserCircle,
} from '@phosphor-icons/react';
import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth';
import { ANDROID_URL, IOS_URL, MOD, PRIVACY_URL, REFUND_URL, TERMS_URL } from '@/lib/format';
import { isTypingTarget, useCategories, useScrolled } from '@/lib/hooks';
import { useT, useUiLang } from '@/lib/i18n';
import { player } from '@/lib/player';
import { supabase } from '@/lib/supabase';
import { chordActive, clearChord, markChordStart } from '@/lib/keys';
import { categoryColor, categoryLabel } from '@/lib/taxonomy';
import { ui } from '@/lib/ui';
import { Overlays } from './overlays';
import { Avatar, Brand, Mark } from './primitives';


function Sidebar() {
  const t = useT();
  const lang = useUiLang();
  const { user } = useAuth();
  const { data: cats } = useCategories();
  const loc = useLocation();
  const nav: { to: string; icon: ReactNode; label: string; kbd?: string }[] = [
    { to: '/', icon: <Compass size={21} />, label: t('nav_discover'), kbd: 'G D' },
    { to: '/today', icon: <SunHorizon size={21} />, label: t('nav_today'), kbd: 'G B' },
    { to: '/markets', icon: <ChartLineUp size={21} />, label: t('nav_markets') },
    { to: '/library', icon: <Books size={21} />, label: t('nav_library') },
    { to: '/sources', icon: <Rss size={21} />, label: t('nav_sources') },
  ];
  return (
    <aside className="sidebar" aria-label="Brifing">
      <Link to="/" aria-label="Brifing">
        <Brand />
      </Link>
      {nav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} title={n.label}>
          {n.icon}
          <span className="nav-label">{n.label}</span>
          {n.kbd && <span className="kbd">{n.kbd}</span>}
        </NavLink>
      ))}
      <div className="nav-section">{t('nav_categories')}</div>
      <div className="nav-cats">
        {(cats ?? []).map((c) => {
          const active = loc.pathname === `/c/${c.slug}`;
          return (
            <Link key={c.id} to={`/c/${c.slug}`} className={`nav-cat${active ? ' active' : ''}`}>
              <span className="dot" style={{ '--c': categoryColor(c.slug) } as CSSProperties} />
              {categoryLabel(lang, c.slug, c.name)}
            </Link>
          );
        })}
      </div>
      <div className="sidebar-foot">
        {!user && (
          <a className="get-app" href={IOS_URL} target="_blank" rel="noreferrer">
            <span className="brand-mark" style={{ width: 36, height: 36 }}>
              <Mark />
            </span>
            <span>
              <strong>{t('nav_get_app')}</strong>
              <span>iOS · Android</span>
            </span>
          </a>
        )}
        <NavLink to={user ? '/settings' : '/signin'} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} title={user ? t('nav_settings') : t('nav_signin')}>
          {user ? <GearSix size={21} /> : <SignIn size={21} />}
          <span className="nav-label">{user ? t('nav_settings') : t('nav_signin')}</span>
        </NavLink>
      </div>
    </aside>
  );
}

function Topbar() {
  const t = useT();
  const scrolled = useScrolled();
  const { user, profile } = useAuth();
  return (
    <header className={`topbar${scrolled ? ' scrolled' : ''}`}>
      <Link to="/" aria-label="Brifing">
        <Brand />
      </Link>
      <button className="search-trigger" onClick={ui.openPalette} aria-label={t('nav_search')}>
        <MagnifyingGlass size={18} />
        <span>{t('search_placeholder')}</span>
        <span className="kbd">{MOD} K</span>
      </button>
      <div className="topbar-right">
        {user ? (
          <Link to="/settings" aria-label={t('nav_profile')}>
            <Avatar name={profile?.full_name ?? user.email} url={profile?.avatar_url} />
          </Link>
        ) : (
          <Link to="/signin" className="btn btn-primary btn-sm">
            {t('nav_signin')}
          </Link>
        )}
      </div>
    </header>
  );
}

function BottomNav() {
  const t = useT();
  const { user } = useAuth();
  const items = [
    { to: '/', icon: <Compass size={23} />, label: t('nav_discover') },
    { to: '/today', icon: <SunHorizon size={23} />, label: t('nav_today') },
    { to: '/markets', icon: <ChartLineUp size={23} />, label: t('nav_markets') },
    { to: '/library', icon: <Books size={23} />, label: t('nav_library') },
    { to: user ? '/settings' : '/signin', icon: <UserCircle size={23} />, label: user ? t('nav_profile') : t('nav_signin') },
  ];
  return (
    <nav className="bottom-nav" aria-label="Brifing">
      {items.map((i) => (
        <NavLink key={i.label} to={i.to} end={i.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
          {i.icon}
          {i.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Footer() {
  const t = useT();
  return (
    <footer className="footer">
      <span>© {new Date().getFullYear()} Brifing · {t('footer_made')}</span>
      <Link to="/pricing">{t('lg_pricing')}</Link>
      <Link to={TERMS_URL}>{t('lg_terms')}</Link>
      <Link to={PRIVACY_URL}>{t('lg_privacy')}</Link>
      <Link to={REFUND_URL}>{t('lg_refund')}</Link>
      <a href="mailto:hello@horusstudios.com">{t('lg_contact')}</a>
      <a href={IOS_URL} target="_blank" rel="noreferrer">
        App Store
      </a>
      <a href={ANDROID_URL} target="_blank" rel="noreferrer">
        Google Play
      </a>
      <button onClick={ui.openShortcuts}>{t('set_shortcuts')}</button>
    </footer>
  );
}

/** Uygulama geneli kısayollar: ⌘K arama, ? yardım, g+d / g+b gezinme, boşluk çal/durdur. */
function useGlobalKeys() {
  const nav = useNavigate();
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ui.openPalette();
        return;
      }
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === '/') {
        e.preventDefault();
        ui.openPalette();
      } else if (e.key === '?') {
        ui.openShortcuts();
      } else if (e.key === 'g') {
        markChordStart();
      } else if (chordActive()) {
        if (e.key === 'd') nav('/');
        if (e.key === 'b') nav('/today');
        if (e.key === 'm') nav('/markets');
        if (e.key === 'l') nav('/library');
        clearChord();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [nav]);
}

/**
 * İngilizce dışı içerik dilinde son 24 saatin kartlarını sunucuda çevirt
 * (translate-recent; içerik×dil başına bir kez, önbellekli). Tarayıcı başına
 * dil başına günde bir kez; çeviri bitince akışlar yeniden çekilir.
 */
function usePrimeContentLang(lang: string) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!lang || lang === 'en' || lang === 'tr') return;
    const key = `brifing-primed:${lang}`;
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (localStorage.getItem(key) === today) return;
      localStorage.setItem(key, today);
    } catch {
      return;
    }
    // Fonksiyon tur başına en fazla 8 içerik çevirir; mobildeki gibi 5 tur.
    void (async () => {
      let total = 0;
      for (let i = 0; i < 5; i++) {
        const { data, error } = await supabase.functions.invoke('translate-recent', { body: { lang, hours: 24, wait: true } });
        const n = (data as { translated?: number } | null)?.translated ?? 0;
        if (error || n === 0) break;
        total += n;
      }
      if (total > 0) await qc.invalidateQueries();
    })().catch(() => undefined);
  }, [lang, qc]);
}

export function Shell() {
  const loc = useLocation();
  const { user, premium, contentLang } = useAuth();
  useGlobalKeys();
  useEffect(() => {
    player.configure({ userId: user?.id ?? null, premium, lang: contentLang });
  }, [user?.id, premium, contentLang]);
  usePrimeContentLang(contentLang);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [loc.pathname]);
  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <Topbar />
        <Outlet />
      </div>
      <BottomNav />
      <Overlays />
    </div>
  );
}
